import { createPaginationTokens } from '../tokens/components.js';
import { SEMANTIC, type SemanticTokens } from '../tokens/semantic.js';

/**
 * Paginator Component Configuration
 *
 * Button specs:
 * - Size: 44px x 44px
 * - Border radius: 8px
 * - Gap: 24px (1.5rem)
 * - Default: white background
 * - Hover: #e7e8ec with shadow
 * - Pressed: #ced1d8 with shadow
 * - Disabled: #b5bac4 with white text
 */
export function createPaginator(s: SemanticTokens) {
  const paginationTokens = createPaginationTokens(s);
  return {
    root: {
      background: 'transparent',
      color: paginationTokens.fgDefault,
      gap: paginationTokens.base.gap,
    },
    navButton: {
      background: paginationTokens.bgDefault,
      hoverBackground: paginationTokens.bgHover,
      selectedBackground: paginationTokens.bgPressed,
      color: paginationTokens.fgDefault,
      hoverColor: paginationTokens.fgHover,
      selectedColor: paginationTokens.fgDefault,
      width: paginationTokens.base.buttonSize,
      height: paginationTokens.base.buttonSize,
      borderRadius: paginationTokens.base.radius,
      focusRing: {
        width: '0',
        style: 'none',
        color: 'transparent',
        offset: '0',
        shadow: 'none',
      },
    },
    currentPageReport: {
      color: paginationTokens.fgDefault,
    },
    colorScheme: {
      light: {
        root: {
          background: 'transparent',
          color: paginationTokens.fgDefault,
        },
        navButton: {
          background: paginationTokens.bgDefault,
          hoverBackground: paginationTokens.bgHover,
          selectedBackground: paginationTokens.bgPressed,
          color: paginationTokens.fgDefault,
          hoverColor: paginationTokens.fgHover,
          selectedColor: paginationTokens.fgDefault,
        },
        currentPageReport: {
          color: paginationTokens.fgDefault,
        },
      },
      dark: {
        root: {
          background: 'transparent',
          color: paginationTokens.fgDefault,
        },
        navButton: {
          background: paginationTokens.bgDefault,
          hoverBackground: paginationTokens.bgHover,
          selectedBackground: paginationTokens.bgPressed,
          color: paginationTokens.fgDefault,
          hoverColor: paginationTokens.fgHover,
          selectedColor: paginationTokens.fgDefault,
        },
        currentPageReport: {
          color: paginationTokens.fgDefault,
        },
      },
    },
    css: () => `
    /* Navigation buttons - add shadow on hover and active */
    .p-paginator .p-paginator-first:not(.p-disabled):hover,
    .p-paginator .p-paginator-prev:not(.p-disabled):hover,
    .p-paginator .p-paginator-next:not(.p-disabled):hover,
    .p-paginator .p-paginator-last:not(.p-disabled):hover {
      box-shadow: ${paginationTokens.shadow};
    }
    
    .p-paginator .p-paginator-first:not(.p-disabled):active,
    .p-paginator .p-paginator-prev:not(.p-disabled):active,
    .p-paginator .p-paginator-next:not(.p-disabled):active,
    .p-paginator .p-paginator-last:not(.p-disabled):active {
      background: ${paginationTokens.bgPressed};
      box-shadow: ${paginationTokens.shadow};
    }
    
    /* Disabled navigation buttons */
    .p-paginator .p-paginator-first.p-disabled,
    .p-paginator .p-paginator-prev.p-disabled,
    .p-paginator .p-paginator-next.p-disabled,
    .p-paginator .p-paginator-last.p-disabled {
      background: ${paginationTokens.bgDisabled};
      color: ${paginationTokens.fgDisabled};
    }
    
    /* Page number buttons - match navigation button styling */
    .p-paginator .p-paginator-page {
      background: ${paginationTokens.bgDefault};
      color: ${paginationTokens.fgDefault};
      border-radius: ${paginationTokens.base.radius};
      width: ${paginationTokens.base.buttonSize};
      height: ${paginationTokens.base.buttonSize};
      min-width: ${paginationTokens.base.buttonSize};
      font-weight: ${paginationTokens.base.fontWeight};
    }
    
    .p-paginator .p-paginator-page:not(.p-disabled):hover {
      background: ${paginationTokens.bgDefault};
      color: ${paginationTokens.fgDefault};
    }
    
    .p-paginator .p-paginator-page:not(.p-disabled):active {
      background: ${paginationTokens.bgDefault};
      color: ${paginationTokens.fgDefault};
    }
    
    .p-paginator .p-paginator-page.p-paginator-page-selected,
    .p-paginator .p-paginator-page.p-paginator-page-selected:hover {
      background: ${paginationTokens.bgDefault};
      color: ${paginationTokens.fgDefault};
      font-weight: ${paginationTokens.base.fontWeight};
    }

    .p-paginator .p-paginator-page.p-disabled {
      background: ${paginationTokens.bgDisabled};
      color: ${paginationTokens.fgDisabled};
    }
    
    /* Icons sizing */
    .p-paginator button .p-icon {
      width: ${paginationTokens.base.iconSize};
      height: ${paginationTokens.base.iconSize};
    }
  `,
  };
}
export const paginator = createPaginator(SEMANTIC);
